import { AxiosError } from 'axios';
import api from './api';

/**
 * Fidelity Payment request payload
 */
interface PaymentData {
  amount: number;
  customerEmail: string;
  customerFirstName: string;
  customerLastName: string;
  customerMobile?: string;
  description?: string;
}

/**
 * Virtual Account details
 */
interface VirtualAccount {
  bankName: string;
  accountNumber: string;
  accountName: string;
  reference: string;
}

/**
 * Fidelity Payment response
 */
interface PaymentResponse {
  success: boolean;
  fundingType?: string;
  paymentId?: string;
  status?: string;
  amount?: number;
  virtualAccount?: VirtualAccount;
  message?: string;
  error?: string;
}

/**
 * Payment status response
 */
interface PaymentStatusResponse {
  success: boolean;
  data?: {
    transactionRef: string;
    status: 'Pending' | 'Processing' | 'Successful' | 'Failed' | 'Cancelled';
    amount: number;
    email: string;
    createdAt: string;
    updatedAt: string;
    message?: string;
  };
  error?: string;
  message?: string;
}

/**
 * Payment history options
 */
interface HistoryOptions {
  status?: string;
  page?: number;
  limit?: number;
}

/**
 * Payment history response
 */
interface PaymentHistoryResponse {
  success: boolean;
  data?: {
    payments: Array<{
      _id: string;
      transactionRef: string;
      status: string;
      amount: number;
      email: string;
      createdAt: string;
    }>;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
    };
  };
  error?: string;
  message?: string;
}

/**
 * Retry response
 */
interface RetryResponse {
  success: boolean;
  data?: {
    paymentId: string;
    status: string;
    message: string;
  };
  error?: string;
  message?: string;
}

class FidelityPaymentService {
  /**
   * Create virtual account for wallet funding
   * @param paymentData - Payment details
   * @returns Response with virtual account details for bank transfer
   */
  static async createVirtualAccount(paymentData: PaymentData): Promise<PaymentResponse> {
    try {
      // Map frontend fields to backend-expected payload
      const body = {
        amount: paymentData.amount,
        description: paymentData.description,
        customerFirstName: paymentData.customerFirstName,
        customerLastName: paymentData.customerLastName,
        customerEmail: paymentData.customerEmail,
        customerMobile: paymentData.customerMobile,
      };

      const response = await api.post<PaymentResponse>(
        `/payments/fidelity/create-virtual-account`,
        body
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<PaymentResponse>;
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message
      };
    }
  }



  /**
   * Get payment status
   * @param transactionRef - Transaction reference
   * @returns Payment status
   */
  static async getPaymentStatus(transactionRef: string): Promise<PaymentStatusResponse> {
    try {
      const response = await api.get<PaymentStatusResponse>(
        `/payments/fidelity/${transactionRef}/status`
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<PaymentStatusResponse>;
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message
      };
    }
  }

  /**
   * Get payment history
   * @param options - Query options (status, page, limit)
   * @returns Payment history
   */
  static async getPaymentHistory(options: HistoryOptions = {}): Promise<PaymentHistoryResponse> {
    try {
      const params = new URLSearchParams(
        Object.entries(options)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)])
      ).toString();
      
      const response = await api.get<PaymentHistoryResponse>(
        `/payments/fidelity/history?${params}`
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<PaymentHistoryResponse>;
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message
      };
    }
  }

  /**
   * Retry a failed payment
   * @param paymentId - Payment ID
   * @returns Retry response
   */
  static async retryPayment(paymentId: string): Promise<RetryResponse> {
    try {
      const response = await api.post<RetryResponse>(
        `/payments/fidelity/${paymentId}/retry`,
        {}
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<RetryResponse>;
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message
      };
    }
  }

  static async getFundingReceipt(paymentId: string): Promise<any> {
    try {
      const response = await api.get(`/payments/fidelity/${paymentId}/receipt`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      return {
        success: false,
        error: axiosError.response?.data?.error || axiosError.message
      };
    }
  }

  static async downloadFundingReceipt(paymentId: string): Promise<void> {
    const response = await api.get(`/payments/fidelity/${paymentId}/receipt/download`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `funding-receipt-${paymentId}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Poll payment status
   * @param transactionRef - Transaction reference
   * @param maxAttempts - Maximum polling attempts
   * @param intervalMs - Interval between polls in milliseconds
   * @returns Final payment status
   */
  static async pollPaymentStatus(
    transactionRef: string,
    maxAttempts: number = 30,
    intervalMs: number = 2000
  ): Promise<any> {
    let attempts = 0;

    return new Promise((resolve) => {
      const pollInterval = setInterval(async () => {
        attempts++;

        const statusResponse = await this.getPaymentStatus(transactionRef);

        if (statusResponse.success) {
          const { status } = statusResponse.data || {};

          // Terminal states
          if (status === 'Successful' || status === 'Failed' || status === 'Cancelled') {
            clearInterval(pollInterval);
            resolve(statusResponse.data);
            return;
          }
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          resolve(statusResponse.data || { status: 'Unknown' });
        }
      }, intervalMs);
    });
  }
}

export default FidelityPaymentService;
export type {
  PaymentData,
  PaymentResponse,
  PaymentStatusResponse,
  HistoryOptions,
  PaymentHistoryResponse,
  RetryResponse,
  VirtualAccount
};
